import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Loading, useBreakpoint } from '@factorialco/deprecated-design-system'
import { ModulesDatoModule } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import Card from 'modules/marketplace_modules/components/PromotionContent/_Card'
import Header from 'modules/marketplace_modules/components/PromotionContent/_Header'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'

import './index.css'

const PromotionContent = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { breakpoint, isMobile } = useBreakpoint()
  const smallScreen = ['xs', 's', 'm', 'l']
  const isSmallScreen = isMobile || smallScreen.includes(breakpoint)
  const [modulesUnavailable, setModulesUnavailable] = useState<ModulesDatoModule[]>([])
  const { moduleAvailabilities } = useModuleAvailabilities()
  const {
    access: { locale: accessLocale },
  } = useCurrentSession()
  const locale = getDatoLocale(accessLocale)
  const { data: modulesData, loading } = useGetDatoModules({
    locale: locale,
  })

  const isModuleUnavailable = (chargebeeModuleName: string) => {
    const availableModule = moduleAvailabilities.filter((obj) => {
      const modulesArray = chargebeeModuleName?.split(',')
      return modulesArray?.includes(obj.id)
    })[0]

    return !(availableModule?.available ?? false)
  }

  useUnsafeEffect(() => {
    if (!loading && modulesData) {
      const unavailableModules: ModulesDatoModule[] = []
      modulesData.forEach((module) => {
        const unavailableModule = isModuleUnavailable(module.chargebeeModuleName || '')
        if (unavailableModule) {
          unavailableModules.push(module)
        }
      })

      // If no unavailable modules found, redirect immediately
      if (unavailableModules.length === 0) {
        navigate(`${navigation.discover.marketplaceModules.fullPath()}/all`, { replace: true })
      } else {
        setModulesUnavailable(unavailableModules)
      }
    }
  }, [loading, modulesData])

  const headerModules = modulesUnavailable.slice(0, 2)
  const listModules = modulesUnavailable.slice(2)

  return (
    <Box
      style={{
        background:
          'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 40%, rgba(240,240,245,0.4) 60%, rgba(245,245,250,0.7) 80%, rgba(250,250,252,0.9) 100%), linear-gradient(90deg, #0a0612 0%, #1a0e1f 30%, #3d1628 60%, #6b2538 100%)',
        minHeight: '100vh',
        width: isSmallScreen ? '100%' : 'fit-content',
        minWidth: isSmallScreen ? '100%' : '1146px',
        maxWidth: isSmallScreen ? 'fit-content' : '1146px',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
      }}
      paddingY="s48"
      paddingLeft="s48"
      borderRadius={{ all: 'abs016' }}
      gap="s48"
    >
      <div className="aurora-layer" />
      <Box style={{ position: 'relative', zIndex: '1' }}>
        <Header modules={headerModules} hideGradient={isSmallScreen} />
        <Box flexDirection="row" gap="s24" flexWrap="wrap" paddingRight="s48">
          {loading ? (
            <Loading />
          ) : (
            listModules.map((module) => (
              <Card
                key={module.id}
                title={module.name || ''}
                description={module.shortDescription || ''}
                isHeaderCard={false}
                slug={module.slug || ''}
                moduleName={module.chargebeeModuleName || ''}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default PromotionContent
